//const API_BASE_URL = "http://localhost:5000/api";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
import logger from "../utils/logger";

if (!API_BASE_URL) {
  throw new Error(
    "API base URL is not defined! Please set REACT_APP_API_BASE_URL in your .env file.",
  );
}

export default API_BASE_URL;

export const createCheckoutSession = async (checkout) => {
  const response = await fetch(
    `${API_BASE_URL}/stripe/create-checkout-session`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(checkout),
    },
  );
  const data = await response.json();
  if (!response.ok || !data.clientSecret) {
    throw new Error(data.error || "Failed to create checkout session");
  }
  return data.clientSecret;
};

export const updateCheckoutShippingOptions = async (
  checkoutSessionId,
  shippingDetails,
) => {
  const response = await fetch(
    `${API_BASE_URL}/stripe/calculate-shipping-options`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        checkout_session_id: checkoutSessionId,
        shipping_details: shippingDetails,
      }),
    },
  );
  const data = await response.json();
  if (!response.ok || data.status !== "ok") {
    throw new Error(data.message || "Failed to update shipping options");
  }
  return data;
};

// No longer using module-level token variable - always fetching from localStorage

// Default fetch options for all requests
const defaultOptions = {
  headers: { "Content-Type": "application/json" },
};

// Helper function to get auth headers
const getAuthHeaders = () => {
  // Always get fresh token from localStorage
  const currentToken = localStorage.getItem("authToken");
  return {
    ...defaultOptions.headers,
    Authorization: currentToken ? `Bearer ${currentToken}` : "",
  };
};

// Helper function to validate image paths/URLs
const isValidImagePath = (path) => {
  if (!path) return false;
  // Allow relative paths starting with /
  if (path.startsWith("/")) return true;
  // Allow absolute URLs
  try {
    new URL(path);
    return true;
  } catch (error) {
    return false;
  }
};

export const register = async (userData) => {
  logger.api("POST", "auth/register");
  try {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const error = await response.json();
      logger.apiError(
        "POST",
        "auth/register",
        new Error(error.message || "Registration failed"),
        {
          status: response.status,
          email: userData.email,
        },
      );
      throw new Error(error.message || "Registration failed");
    }

    const data = await response.json();
    logger.auth("User registered successfully", { email: userData.email });

    // Set the token in localStorage only
    localStorage.setItem("authToken", data.token);

    // Get the user profile using the new token
    logger.api("GET", "user/profile", { context: "post-registration" });
    const profileResponse = await fetch(`${API_BASE_URL}/user/profile`, {
      ...defaultOptions,
      headers: getAuthHeaders(),
    });

    if (!profileResponse.ok) {
      logger.apiError(
        "GET",
        "user/profile",
        new Error("Failed to fetch user profile after registration"),
      );
      throw new Error("Failed to fetch user profile after registration");
    }

    const profile = await profileResponse.json();
    logger.debug("User profile fetched after registration", {
      userId: profile._id,
    });
    return profile;
  } catch (error) {
    logger.apiError("POST", "auth/register", error);
    throw error;
  }
};

export const login = async (email, password) => {
  logger.api("POST", "auth/login", { email });
  try {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      logger.apiError(
        "POST",
        "auth/login",
        new Error(error.message || "Invalid credentials"),
        {
          status: response.status,
          email,
        },
      );
      throw new Error(error.message || "Invalid credentials");
    }

    const data = await response.json();
    localStorage.setItem("authToken", data.token);
    logger.auth("User logged in successfully", {
      email,
      userId: data.user?._id,
    });

    return data;
  } catch (error) {
    logger.apiError("POST", "auth/login", error, { email });
    throw error;
  }
};

export const logout = () => {
  logger.auth("User logged out");
  localStorage.removeItem("authToken");
};

export const getProducts = async () => {
  const response = await fetch(`${API_BASE_URL}/printful/products`);
  if (!response.ok) {
    throw new Error("Failed to fetch products");
  }
  return decoratePrintful(await response.json());
};

// Decorate each product so downstream code knows it came from Printful
export const decoratePrintful = (arr) =>
  arr.map((p) => ({ ...p, printful: true }));

export const getProductsByCharacter = async (character) => {
  const response = await fetch(
    `${API_BASE_URL}/printful/products/character/${encodeURIComponent(character)}`,
  );
  if (!response.ok) {
    throw new Error("Failed to fetch products by character");
  }
  return decoratePrintful(await response.json());
};

export const getProductsByCategory = async (category) => {
  const response = await fetch(
    `${API_BASE_URL}/printful/products/category/${encodeURIComponent(category)}`,
  );
  if (!response.ok) {
    throw new Error("Failed to fetch products by category");
  }
  return decoratePrintful(await response.json());
};

export const getCharacters = async () => {
  const response = await fetch(`${API_BASE_URL}/characters`);
  if (!response.ok) {
    throw new Error("Failed to fetch characters");
  }
  return response.json();
};

// Admin API functions
export const getAllProducts = async () => {
  const currentToken = localStorage.getItem("authToken");
  if (!currentToken) {
    throw new Error("Not authenticated");
  }

  // Fetch all products via admin endpoint
  const response = await fetch(`${API_BASE_URL}/admin/products`, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error("Failed to fetch all products");
  }

  return response.json();
};

// Utility function to extract character tag and clean product name
const extractCharacterAndName = (productName) => {
  // First remove variant details after separator (/ or - followed by size info)
  const baseNameMatch =
    productName && typeof productName.split === "function"
      ? productName.split(/\s*[/-]\s*(?:11|15|20)\s*(?:oz|ounce)/i)[0]
      : "";

  // Then look for character tag anywhere in the remaining name
  const tagMatch =
    typeof baseNameMatch === "string"
      ? baseNameMatch.match(/\[([^\]]+)\]/)
      : null;
  if (!tagMatch) {
    return { name: (baseNameMatch || "").trim(), characterTag: null };
  }

  // Remove the character tag and clean up any extra spaces
  const name = baseNameMatch
    .replace(tagMatch[0], "")
    .replace(/\s+/g, " ")
    .trim();

  return {
    name,
    characterTag: tagMatch[1].trim().toUpperCase(),
  };
};

// Sync all Printful products
export const syncPrintfulProducts = async () => {
  const currentToken = localStorage.getItem("authToken");
  if (!currentToken) {
    throw new Error("Not authenticated");
  }

  try {
    const response = await fetch(`${API_BASE_URL}/printful/products/sync`, {
      method: "POST",
      headers: {
        ...getAuthHeaders(),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: "Failed to sync Printful products",
      }));
      throw new Error(errorData.message);
    }

    return response.json();
  } catch (error) {
    if (process.env.NODE_ENV !== "production")
      logger.apiError("POST", "printful/sync", error);
    throw error;
  }
};

// Delete a product and its associated images (Admin only)
export const deleteProduct = async (productId) => {
  logger.api("DELETE", `products/${productId}`);
  try {
    const response = await fetch(
      `${API_BASE_URL}/admin/products/${productId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to delete product");
    }

    const result = await response.json();
    logger.info("Product deleted successfully", { result });
    return result;
  } catch (error) {
    logger.apiError("DELETE", "products", error);
    throw error;
  }
};

export const deleteProductGroup = async (syncProductId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/admin/products/group/${syncProductId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to delete product group");
    }

    const result = await response.json();
    logger.info("Product group deleted successfully", { result });
    return result;
  } catch (error) {
    logger.apiError("DELETE", "products/group", error);
    throw error;
  }
};

// Update Printful product metadata
export const updatePrintfulProduct = async (productData) => {
  const currentToken = localStorage.getItem("authToken");
  if (!currentToken) {
    throw new Error("Not authenticated");
  }

  // Validate image paths
  if (productData.image && !isValidImagePath(productData.image)) {
    throw new Error("Invalid image path format");
  }
  if (productData.additionalImages) {
    for (const path of productData.additionalImages) {
      if (!isValidImagePath(path)) {
        throw new Error("Invalid image path format in additional images");
      }
    }
  }

  // Use the most reliable ID available - prefer _id, then external_id, then sync_product_id
  const productId =
    productData._id ||
    productData.id ||
    productData.external_id ||
    productData.sync_product_id;

  if (!productId) {
    throw new Error("No valid product ID found for update");
  }

  if (process.env.NODE_ENV !== "production")
    console.debug(`Updating product with ID: ${productId}`, {
      idType: productData._id
        ? "_id"
        : productData.id
          ? "id"
          : productData.external_id
            ? "external_id"
            : "sync_product_id",
      updateData: {
        name: productData.name,
        description: productData.description,
        isVisible: productData.isVisible,
        // other fields...
      },
    });

  try {
    const response = await fetch(
      `${API_BASE_URL}/printful/products/${productId}`,
      {
        method: "PATCH",
        headers: {
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          name: productData.name,
          description: productData.description,
          additionalImages: productData.additionalImages,
          character: productData.character,
          category: productData.category,
          isVisible: productData.isVisible,
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => {
        return {
          message: `Failed to update product (Status: ${response.status})`,
        };
      });
      throw new Error(
        errorData?.message || `Failed to update product with ID ${productId}`,
      );
    }

    return response.json();
  } catch (error) {
    if (process.env.NODE_ENV !== "production")
      console.error(`Error updating product ${productId}:`, error);
    throw error;
  }
};

export const updateProduct = async (productData) => {
  const currentToken = localStorage.getItem("authToken");
  if (!currentToken) {
    throw new Error("Not authenticated");
  }

  // Validate image paths
  if (productData.image && !isValidImagePath(productData.image)) {
    throw new Error("Invalid image path format");
  }
  if (productData.additionalImages) {
    for (const path of productData.additionalImages) {
      if (!isValidImagePath(path)) {
        throw new Error("Invalid image path format in additional images");
      }
    }
  }

  // For non-Printful products, use the same route but include different fields
  const productId =
    productData._id ||
    productData.id ||
    productData.external_id ||
    productData.sync_product_id;

  if (!productId) {
    throw new Error("No valid product ID found for update");
  }

  const response = await fetch(
    `${API_BASE_URL}/printful/products/${productId}`,
    {
      method: "PATCH",
      headers: {
        ...getAuthHeaders(),
      },
      body: JSON.stringify({
        name: productData.name,
        description: productData.description,
        price: productData.price,
        image: productData.image,
        variants: productData.variants,
        additionalImages: productData.additionalImages,
        character: productData.character,
        category: productData.category,
        inStock: productData.inStock,
        isVisible: productData.isVisible,
      }),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || "Failed to update product");
  }

  return response.json();
};

// User Profile Management
export const getUserProfile = async () => {
  logger.api("GET", "user/profile");
  const currentToken = localStorage.getItem("authToken");
  if (!currentToken) {
    logger.apiError("GET", "user/profile", new Error("Not authenticated"));
    throw new Error("Not authenticated");
  }

  try {
    const response = await fetch(`${API_BASE_URL}/user/profile`, {
      ...defaultOptions,
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      logger.apiError(
        "GET",
        "user/profile",
        new Error("Failed to fetch user profile"),
        {
          status: response.status,
          statusText: response.statusText,
        },
      );
      throw new Error("Failed to fetch user profile");
    }

    const profile = await response.json();
    logger.debug("User profile fetched successfully", { userId: profile._id });
    return profile;
  } catch (error) {
    logger.apiError("GET", "user/profile", error);
    throw error;
  }
};

export const updateUserProfile = async (profileData) => {
  const currentToken = localStorage.getItem("authToken");
  if (!currentToken) throw new Error("Not authenticated");

  const response = await fetch(`${API_BASE_URL}/user/profile`, {
    ...defaultOptions,
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify(profileData),
  });
  if (!response.ok) {
    throw new Error("Failed to update profile");
  }
  return response.json();
};

// Address Management
export const addAddress = async (addressData) => {
  const currentToken = localStorage.getItem("authToken");
  if (!currentToken) throw new Error("Not authenticated");

  const response = await fetch(`${API_BASE_URL}/user/addresses`, {
    ...defaultOptions,
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(addressData),
  });
  if (!response.ok) {
    throw new Error("Failed to add address");
  }
  return response.json();
};

export const updateAddress = async (addressId, addressData) => {
  const currentToken = localStorage.getItem("authToken");
  if (!currentToken) throw new Error("Not authenticated");

  const response = await fetch(`${API_BASE_URL}/user/addresses/${addressId}`, {
    ...defaultOptions,
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify(addressData),
  });
  if (!response.ok) {
    throw new Error("Failed to update address");
  }
  return response.json();
};

export const deleteAddress = async (addressId) => {
  const currentToken = localStorage.getItem("authToken");
  if (!currentToken) throw new Error("Not authenticated");

  const response = await fetch(`${API_BASE_URL}/user/addresses/${addressId}`, {
    ...defaultOptions,
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error("Failed to delete address");
  }
  return response.json();
};

export const setDefaultAddress = async (addressId) => {
  const currentToken = localStorage.getItem("authToken");
  if (!currentToken) throw new Error("Not authenticated");

  const response = await fetch(
    `${API_BASE_URL}/user/addresses/${addressId}/default`,
    {
      ...defaultOptions,
      method: "PATCH",
      headers: getAuthHeaders(),
    },
  );
  if (!response.ok) {
    throw new Error("Failed to set default address");
  }
  return response.json();
};

// Password Reset
export const requestPasswordReset = async (email) => {
  const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
    ...defaultOptions,
    method: "POST",
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    throw new Error("Failed to request password reset");
  }
  return response.json();
};

export const resetPassword = async (token, newPassword) => {
  const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
    ...defaultOptions,
    method: "POST",
    body: JSON.stringify({ token, newPassword }),
  });
  if (!response.ok) {
    throw new Error("Failed to reset password");
  }
  return response.json();
};

// Email Verification
export const verifyEmail = async (token) => {
  const response = await fetch(`${API_BASE_URL}/auth/verify-email/${token}`, {
    ...defaultOptions,
    method: "GET",
  });
  if (!response.ok) {
    throw new Error("Failed to verify email");
  }
  return response.json();
};

export const resendVerificationEmail = async () => {
  const currentToken = localStorage.getItem("authToken");
  if (!currentToken) throw new Error("Not authenticated");

  const response = await fetch(`${API_BASE_URL}/auth/resend-verification`, {
    ...defaultOptions,
    method: "POST",
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error("Failed to resend verification email");
  }
  return response.json();
};

/**
 * Fetch the current user's orders
 * @returns {Promise<Array>} Array of order objects
 * @throws {Error} If authentication fails or API request fails
 */
export const getOrders = async () => {
  logger.api("GET", "stripe/orders");
  const currentToken = localStorage.getItem("authToken");
  if (!currentToken) {
    logger.apiError("GET", "stripe/orders", new Error("Not authenticated"));
    throw new Error("Not authenticated");
  }

  try {
    const response = await fetch(`${API_BASE_URL}/stripe/orders`, {
      ...defaultOptions,
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const statusText = response.statusText || "Unknown error";
      const errorMessage =
        errorData.error ||
        errorData.message ||
        `Failed to fetch orders: ${statusText}`;

      logger.apiError("GET", "stripe/orders", new Error(errorMessage), {
        statusCode: response.status,
        statusText: response.statusText,
      });

      if (response.status === 401 || response.status === 403) {
        throw new Error("Not authenticated");
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();
    logger.debug(`Successfully fetched ${data.length} orders`);
    return data;
  } catch (error) {
    logger.apiError("GET", "stripe/orders", error);
    throw error;
  }
};

/**
 * Fetch a specific order by its payment intent ID
 * @param {string} paymentIntentId - The Stripe payment intent ID
 * @returns {Promise<Object>} Order object
 * @throws {Error} If authentication fails or API request fails
 */
export const getOrderByPaymentIntent = async (paymentIntentId) => {
  logger.api("GET", `stripe/order/${paymentIntentId}`, { paymentIntentId });
  const currentToken = localStorage.getItem("authToken");
  if (!currentToken) {
    logger.apiError(
      "GET",
      `stripe/order/${paymentIntentId}`,
      new Error("Not authenticated"),
    );
    throw new Error("Not authenticated");
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/stripe/order/${paymentIntentId}`,
      {
        ...defaultOptions,
        headers: getAuthHeaders(),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const statusText = response.statusText || "Unknown error";
      const errorMessage =
        errorData.error ||
        errorData.message ||
        `Failed to fetch order: ${statusText}`;

      logger.apiError(
        "GET",
        `stripe/order/${paymentIntentId}`,
        new Error(errorMessage),
        {
          statusCode: response.status,
          statusText: response.statusText,
          paymentIntentId,
        },
      );

      if (response.status === 401 || response.status === 403) {
        throw new Error("Not authenticated");
      } else if (response.status === 404) {
        throw new Error("Order not found");
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();
    logger.debug(`Successfully fetched order details`, {
      orderId: data._id,
      paymentIntentId,
    });
    return data;
  } catch (error) {
    logger.apiError("GET", `stripe/order/${paymentIntentId}`, error, {
      paymentIntentId,
    });
    throw error;
  }
};

export const getColors = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/colors`);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch colors: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();

    // Ensure the response has the expected structure
    return {
      colors: Array.isArray(data.colors) ? data.colors : [],
      combinations: Array.isArray(data.combinations) ? data.combinations : [],
    };
  } catch (error) {
    if (process.env.NODE_ENV !== "production")
      logger.apiError("GET", "colors", error);
    // Return default empty arrays to prevent undefined errors
    return { colors: [], combinations: [] };
  }
};

/**
 * Fetch the featured Ryldo product from Stripe
 * @returns {Promise<Object>} The Ryldo product with Stripe pricing information
 */
export const getFeaturedRyldoProduct = async () => {
  logger.api("GET", "products/featured/ryldo");
  try {
    const response = await fetch(`${API_BASE_URL}/products/featured/ryldo`);

    if (!response.ok) {
      const errorMessage = `Failed to fetch Ryldo product: ${response.status} ${response.statusText}`;
      logger.apiError(
        "GET",
        "products/featured/ryldo",
        new Error(errorMessage),
      );
      throw new Error(errorMessage);
    }

    const product = await response.json();
    logger.debug("Fetched featured Ryldo product", {
      productId: product.stripeId,
    });
    return product;
  } catch (error) {
    logger.apiError("GET", "products/featured/ryldo", error);
    throw error;
  }
};

/**
 * Fetch all custom products (non-Printful) from Stripe
 * @returns {Promise<Array>} Array of custom product objects
 */
export const getCustomProducts = async () => {
  logger.api("GET", "products/custom");
  try {
    const response = await fetch(`${API_BASE_URL}/products/custom`);

    if (!response.ok) {
      const errorMessage = `Failed to fetch custom products: ${response.status} ${response.statusText}`;
      logger.apiError("GET", "products/custom", new Error(errorMessage));
      throw new Error(errorMessage);
    }

    const products = await response.json();
    logger.debug(`Fetched ${products.length} custom products`);
    return products;
  } catch (error) {
    logger.apiError("GET", "products/custom", error);
    throw error;
  }
};

/**
 * Fetch a specific Stripe product by ID
 * @param {string} stripeId - The Stripe product ID
 * @returns {Promise<Object>} Stripe product object
 */
export const getStripeProduct = async (stripeId) => {
  logger.api("GET", `products/stripe/${stripeId}`, { stripeId });
  try {
    const response = await fetch(`${API_BASE_URL}/products/stripe/${stripeId}`);

    if (!response.ok) {
      const errorMessage = `Failed to fetch Stripe product: ${response.status} ${response.statusText}`;
      logger.apiError(
        "GET",
        `products/stripe/${stripeId}`,
        new Error(errorMessage),
      );
      throw new Error(errorMessage);
    }

    const product = await response.json();
    logger.debug("Fetched Stripe product", {
      productId: product.stripeId,
      name: product.name,
    });
    return product;
  } catch (error) {
    logger.apiError("GET", `products/stripe/${stripeId}`, error);
    throw error;
  }
};

/**
 * Admin function to sync all Stripe products
 * @returns {Promise<Object>} Result of the sync operation
 */
export const syncStripeProducts = async () => {
  logger.api("POST", "products/stripe/sync");
  try {
    const response = await fetch(`${API_BASE_URL}/products/stripe/sync`, {
      ...defaultOptions,
      method: "POST",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const errorMessage =
        error.message ||
        `Failed to sync Stripe products: ${response.status} ${response.statusText}`;
      logger.apiError("POST", "products/stripe/sync", new Error(errorMessage));
      throw new Error(errorMessage);
    }

    const result = await response.json();
    logger.debug("Stripe products synced successfully");
    return result;
  } catch (error) {
    logger.apiError("POST", "products/stripe/sync", error);
    throw error;
  }
};

/**
 * Fetch Printful order details by order ID
 * @param {string} orderId - The Printful order ID
 * @returns {Promise<Object>} Printful order object with status and details
 * @throws {Error} If authentication fails or API request fails
 */
export const getPrintfulOrder = async (orderId) => {
  logger.api("GET", `printful/orders/${orderId}`, { orderId });

  const authToken = localStorage.getItem("authToken");
  if (!authToken) {
    const error = new Error("Not authenticated");
    logger.apiError("GET", `printful/orders/${orderId}`, error);
    throw error;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/printful/orders/${orderId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage =
        errorData.message ||
        `Failed to fetch Printful order: ${response.status} ${response.statusText}`;

      logger.apiError(
        "GET",
        `printful/orders/${orderId}`,
        new Error(errorMessage),
        {
          status: response.status,
          orderId,
        },
      );

      if (response.status === 404) {
        throw new Error("Printful order not found");
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || "Failed to fetch Printful order");
    }

    logger.debug("Successfully fetched Printful order", {
      orderId,
      status: data.order?.status,
    });

    return data.order;
  } catch (error) {
    logger.apiError("GET", `printful/orders/${orderId}`, error, { orderId });
    throw error;
  }
};

/**
 * Confirm a Printful order by order ID (Admin only)
 * @param {string} orderId - The Printful order ID
 * @returns {Promise<Object>} Confirmed Printful order object
 * @throws {Error} If authentication fails or API request fails
 */
export const confirmPrintfulOrder = async (orderId) => {
  logger.api("PUT", `printful/orders/${orderId}/confirm`, { orderId });

  const authToken = localStorage.getItem("authToken");
  if (!authToken) {
    const error = new Error("Not authenticated");
    logger.apiError("PUT", `printful/orders/${orderId}/confirm`, error);
    throw error;
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/printful/orders/${orderId}/confirm`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage =
        errorData.message ||
        `Failed to confirm Printful order: ${response.status} ${response.statusText}`;

      logger.apiError(
        "PUT",
        `printful/orders/${orderId}/confirm`,
        new Error(errorMessage),
        {
          status: response.status,
          orderId,
        },
      );

      if (response.status === 404) {
        throw new Error("Printful order not found");
      } else if (response.status === 400) {
        throw new Error(errorMessage);
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || "Failed to confirm Printful order");
    }

    logger.debug("Successfully confirmed Printful order", {
      orderId,
      status: data.order?.status,
    });

    return data.order;
  } catch (error) {
    logger.apiError("PUT", `printful/orders/${orderId}/confirm`, error, {
      orderId,
    });
    throw error;
  }
};
